import { formatDateOnly, parseDateOnly } from "../../../../_shared/_logic/timezones"

export type BatchDateRangeFilter = {
  start: string
  end: string
}

export type BatchSelectOption = {
  value: string
  label: string
}

export const normalizeBatchSearch = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLocaleLowerCase("de-DE")

export const textMatchesBatchSearch = (search: string, values: unknown[]) => {
  const needle = normalizeBatchSearch(search)

  if (!needle) return true

  return values.some((value) => normalizeBatchSearch(value).includes(needle))
}

export const normalizeBatchDateRange = (value: unknown): BatchDateRangeFilter => {
  if (!value || typeof value !== "object") return { start: "", end: "" }

  const source = value as { start?: unknown; end?: unknown; from?: unknown; to?: unknown }
  const start =
    typeof source.start === "string"
      ? source.start
      : typeof source.from === "string"
        ? source.from
        : ""
  const end =
    typeof source.end === "string" ? source.end : typeof source.to === "string" ? source.to : ""

  return { start, end }
}

export const batchDateRangeInputValue = (range: BatchDateRangeFilter) =>
  range.start || range.end ? range : null

export const dateFromBatchValues = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value !== "string" || !value.trim()) continue

    const parsed = parseDateOnly(value)

    if (parsed) return parsed
  }

  return null
}

export const hasUsefulBatchSearch = (rowCount: number) => rowCount > 1

export const hasUsefulBatchSelectOptions = (options: BatchSelectOption[], rowCount: number) => {
  if (rowCount <= 1) return false

  const realValues = new Set(options.map((option) => option.value.trim()).filter(Boolean))

  return realValues.size > 1
}

export const hasUsefulBatchDateRange = (values: unknown[], rowCount = values.length) => {
  if (rowCount <= 1) return false

  const days = new Set(
    values
      .map((value) => dateFromBatchValues(value))
      .filter((date): date is Date => Boolean(date))
      .map((date) => formatDateOnly(date)),
  )

  return days.size > 1
}

export const dateMatchesBatchRange = (date: Date | null, range: BatchDateRangeFilter) => {
  if (!range.start && !range.end) return true

  if (!date) return false

  if (range.start) {
    const start = parseDateOnly(range.start)

    if (start && date < start) return false
  }

  if (range.end) {
    const end = parseDateOnly(range.end)

    if (end) {
      end.setHours(23, 59, 59, 999)

      if (date > end) return false
    }
  }

  return true
}

export const uniqueBatchOptions = (
  values: Array<string | null | undefined>,
  allLabel: string,
): BatchSelectOption[] => {
  const unique = Array.from(
    new Set(
      values.map((value) => value?.trim()).filter((value): value is string => Boolean(value)),
    ),
  )

  return [
    { value: "", label: allLabel },
    ...unique.sort((a, b) => a.localeCompare(b, "de-DE")).map((value) => ({ value, label: value })),
  ]
}
